/**
 * Recruitment ATS — API client (Phase 1)
 *
 * Types are generated by hand from the authoritative contract
 * `docs/recruitment/recruitment-phase1.openapi.json`. Do not invent shapes —
 * if the backend changes, update the contract first, then these types.
 *
 * Every response is wrapped in the standard envelope { status, status_code, data }.
 * `apiService.*` returns that envelope; always read `.data` for the payload.
 * Backend base path: `${VITE_API_BASE_URL}/recruitment` (i.e. /v1/api/recruitment).
 */

import { apiService, ApiResponse } from './api';

const BASE = '/recruitment';

// ─── Enums ───────────────────────────────────────────────────────────────────

export type JobStatus = 'open' | 'on_hold' | 'closed' | 'filled';
export type EmploymentType = 'full_time' | 'part_time' | 'contract' | 'intern';
export type Stage =
	| 'new'
	| 'screened'
	| 'shortlisted'
	| 'interview'
	| 'offer'
	| 'hired'
	| 'rejected'
	| 'on_hold';

/** Outcome of the cheap pre-check (`POST /candidates/check-duplicate`). */
export type DedupOutcome = 'new' | 'matched' | 'ambiguous';
/** Outcome of the real intake (`POST /candidates`). Drives the UI branch. */
export type IntakeOutcome = 'created' | 'linked' | 'ambiguous';

// ─── Shared ──────────────────────────────────────────────────────────────────

export interface Qualifications {
	required?: string[];
	preferred?: string[];
}

export interface Paginated<Row> {
	page: number;
	page_size: number;
	total: number;
	rows: Row[];
}

// ─── Job Openings ────────────────────────────────────────────────────────────

export interface JobOpeningCreate {
	title: string; // required
	employment_type?: EmploymentType;
	openings_count?: number; // int ≥ 1
	department_id?: number | null;
	facility_key?: string | null;
	experience_min?: number | null; // years (sent as number)
	experience_max?: number | null;
	qualifications?: Qualifications;
	jd_text?: string | null;
	hiring_manager_id?: number | null;
	status?: JobStatus;
	date_opened?: string; // YYYY-MM-DD
}

/** Partial update — any subset (min 1 field); title not required. */
export type JobOpeningUpdate = Partial<JobOpeningCreate>;

export interface JobOpening {
	id: number;
	title: string;
	employment_type: EmploymentType;
	openings_count: number;
	department_id: number | null;
	facility_key: string | null;
	/** DECIMAL — returned as a string, e.g. "0.0". Parse before formatting. */
	experience_min: string | null;
	experience_max: string | null;
	qualifications: Qualifications | null;
	jd_text: string | null;
	hiring_manager_id: number | null;
	status: JobStatus;
	date_opened: string;
	/** Present on list rows only. */
	application_count?: number;
	created_at?: string;
	updated_at?: string;
}

export interface JobOpeningFilters {
	status?: JobStatus;
	hiring_manager_id?: number;
	facility_key?: string;
	q?: string; // title search
	page?: number;
	page_size?: number; // ≤ 200
}

export type JobOpeningPage = Paginated<JobOpening>;

// ─── Candidates & intake ─────────────────────────────────────────────────────

export interface IntakeBody {
	full_name: string; // required
	phone?: string | null; // phone OR email required
	email?: string | null;
	job_opening_id: number; // required
	source: string; // required
	recruiter_id?: number | null;
	location?: string | null;
	total_experience?: number | null; // years
	current_company?: string | null;
	current_role?: string | null;
	notice_period_days?: number | null;
	current_ctc?: number | null; // CTC-restricted on read
	expected_ctc?: number | null;
}

export interface Candidate {
	id: number;
	full_name: string;
	phone_raw: string | null;
	email_raw: string | null;
	location: string | null;
	/** DECIMAL as string, e.g. "2.0". */
	total_experience: string | null;
	current_company: string | null;
	current_role: string | null;
	notice_period_days: number | null;
	/**
	 * CTC keys are ABSENT (not null) when the caller is not CTC-authorized.
	 * Use `hasOwnProperty` / `in` — an absent key means "🔒 Restricted",
	 * a present-but-null value means "not provided".
	 */
	current_ctc?: number | null;
	expected_ctc?: number | null;
	created_at?: string;
	updated_at?: string;
}

export interface Application {
	id: number;
	candidate_id: number;
	job_opening_id: number;
	source: string;
	applied_date: string;
	stage: Stage;
	recruiter_id: number | null;
	created_at?: string;
	updated_at?: string;
}

/** An application list row — Application plus joined candidate/job/recruiter names. */
export interface ApplicationRow extends Application {
	candidate_name: string;
	job_title: string;
	recruiter_first_name: string | null;
	recruiter_last_name: string | null;
}

export type ApplicationPage = Paginated<ApplicationRow>;

export interface CheckDuplicateBody {
	phone?: string;
	email?: string;
}

export interface CheckDuplicateResult {
	outcome: DedupOutcome;
	candidate_id?: number | null;
	candidate_ids?: number[] | null;
}

export interface IntakeCreated {
	outcome: 'created';
	matched: false;
	candidate: Candidate;
	application: Application;
}

export interface IntakeLinked {
	outcome: 'linked';
	matched: true;
	candidate: Candidate;
	application: Application;
	priorApplications: ApplicationRow[];
}

export interface IntakeAmbiguous {
	outcome: 'ambiguous';
	/** The possible people — the user must pick one, then call linkApplication. */
	candidates: Candidate[];
}

export type IntakeResult = IntakeCreated | IntakeLinked | IntakeAmbiguous;

export interface CandidateDetail {
	candidate: Candidate;
	applications: ApplicationRow[];
}

export interface LinkApplicationBody {
	job_opening_id: number;
	source: string;
	recruiter_id?: number | null;
}

export interface LinkApplicationResult {
	outcome: 'linked';
	application: Application;
}

export interface ResumeUploadResult {
	resume_id: number;
	text_indexed: boolean;
}

// ─── Applications (pipeline) ─────────────────────────────────────────────────

export interface ApplicationFilters {
	job_opening_id?: number;
	recruiter_id?: number;
	stage?: Stage;
	source?: string;
	q?: string; // candidate name
	page?: number;
	page_size?: number;
}

export interface StageHistoryRow {
	from_stage: string | null;
	to_stage: string;
	reason: string | null;
	performed_by: number | null;
	by_first_name: string | null;
	by_last_name: string | null;
	performed_at: string;
}

export interface ApplicationDetail {
	application: Application;
	stage_history: StageHistoryRow[];
}

export interface MoveStageBody {
	to: Stage;
	reason?: string; // optional at API, MANDATORY in the UI (rule R3)
}

// ─── Resume search ───────────────────────────────────────────────────────────

export interface ResumeSearchResultItem {
	candidate_id: number;
	full_name: string;
	current_company: string | null;
	current_role: string | null;
	location: string | null;
	total_experience: string | null;
	score: number;
	snippet: string;
	filename: string | null;
	resume_updated_at: string;
}

export interface ResumeSearchResult {
	query: string;
	count: number;
	results: ResumeSearchResultItem[];
}

// ─── Query-string helper ─────────────────────────────────────────────────────

function toQuery(params: Record<string, unknown>): string {
	const sp = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === '') return;
		sp.append(key, String(value));
	});
	const qs = sp.toString();
	return qs ? `?${qs}` : '';
}

// ─── Service ─────────────────────────────────────────────────────────────────

export class RecruitmentApiService {
	// ── Job Openings ──────────────────────────────────────────────────────────

	static listJobs(filters: JobOpeningFilters = {}): Promise<ApiResponse<JobOpeningPage>> {
		return apiService.get(`${BASE}/jobs${toQuery(filters as Record<string, unknown>)}`);
	}

	static getJob(id: number): Promise<ApiResponse<JobOpening>> {
		return apiService.get(`${BASE}/jobs/${id}`);
	}

	static createJob(data: JobOpeningCreate): Promise<ApiResponse<JobOpening>> {
		return apiService.post(`${BASE}/jobs`, data);
	}

	static updateJob(id: number, data: JobOpeningUpdate): Promise<ApiResponse<JobOpening>> {
		return apiService.patch(`${BASE}/jobs/${id}`, data);
	}

	// ── Candidate intake + dedup ──────────────────────────────────────────────

	static checkDuplicate(body: CheckDuplicateBody): Promise<ApiResponse<CheckDuplicateResult>> {
		return apiService.post(`${BASE}/candidates/check-duplicate`, body);
	}

	/**
	 * The real intake. Runs dedup, then creates or links.
	 * Success is 2xx for all three outcomes (201 created, 200 linked/ambiguous) —
	 * branch on `data.outcome`, never on the HTTP status alone.
	 */
	static intake(body: IntakeBody): Promise<ApiResponse<IntakeResult>> {
		return apiService.post(`${BASE}/candidates`, body);
	}

	static getCandidate(id: number): Promise<ApiResponse<CandidateDetail>> {
		return apiService.get(`${BASE}/candidates/${id}`);
	}

	/** Add an application to an EXISTING candidate (after an ambiguous pick, or "add to existing person"). */
	static linkApplication(
		candidateId: number,
		body: LinkApplicationBody
	): Promise<ApiResponse<LinkApplicationResult>> {
		return apiService.post(`${BASE}/candidates/${candidateId}/applications`, body);
	}

	/** Upload a resume (multipart, field name `file`). PDFs are not text-indexed in Phase 1. */
	static uploadResume(
		candidateId: number,
		file: File,
		extractedText?: string
	): Promise<ApiResponse<ResumeUploadResult>> {
		const formData = new FormData();
		formData.append('file', file);
		if (extractedText) formData.append('extracted_text', extractedText);
		return apiService.postFormData(`${BASE}/candidates/${candidateId}/resume`, formData);
	}

	// ── Applications (pipeline) ───────────────────────────────────────────────

	static listApplications(filters: ApplicationFilters = {}): Promise<ApiResponse<ApplicationPage>> {
		return apiService.get(`${BASE}/applications${toQuery(filters as Record<string, unknown>)}`);
	}

	static getApplication(id: number): Promise<ApiResponse<ApplicationDetail>> {
		return apiService.get(`${BASE}/applications/${id}`);
	}

	/** Move a stage. Returns the same shape as getApplication (updated application + full history). */
	static moveStage(id: number, body: MoveStageBody): Promise<ApiResponse<ApplicationDetail>> {
		return apiService.patch(`${BASE}/applications/${id}/stage`, body);
	}

	// ── Resume search ─────────────────────────────────────────────────────────

	static searchResumes(q: string): Promise<ApiResponse<ResumeSearchResult>> {
		return apiService.get(`${BASE}/resumes/search${toQuery({ q })}`);
	}
}
